import { FC, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Autocomplete,
  Chip,
  Alert,
  Link,
} from '@mui/material';
import { Close } from '@mui/icons-material';

import { useContactRequestCreateMutation } from '@carcareers/store';
import { KEYWORDS } from '@carcareers/utils';

interface ContactRequestModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  title: string;
  businessName: string;
  phone: string;
  email: string;
  positions: string[];
}

const initialFormData: FormData = {
  name: '',
  title: '',
  businessName: '',
  phone: '',
  email: '',
  positions: [],
};

export const ContactRequestModal: FC<ContactRequestModalProps> = ({ open, onClose }) => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [submitted, setSubmitted] = useState(false);
  const [createContactRequest, { isLoading }] = useContactRequestCreateMutation();

  const handleInputChange = (field: keyof FormData, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isFormValid =
    formData.name.trim() &&
    formData.title.trim() &&
    formData.businessName.trim() &&
    formData.phone.trim() &&
    formData.email.trim();

  const handleSubmit = async () => {
    if (!isFormValid) return;

    try {
      await createContactRequest({
        name: formData.name.trim(),
        title: formData.title.trim(),
        businessName: formData.businessName.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        positions: formData.positions,
      }).unwrap();

      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit contact request:', error);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleExited = () => {
    setFormData(initialFormData);
    setSubmitted(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth TransitionProps={{ onExited: handleExited }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4">Talk to a Representative</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
            Call{' '}
            <Link href="tel:8883979797" underline="hover" sx={{ color: 'primary.main', fontWeight: 600 }}>
              (888) 397-9797
            </Link>{' '}
            for your FREE Quote or submit your request and we will call you.
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ mt: 0.5, ml: 2, flexShrink: 0 }}>
          <Close />
        </IconButton>
      </DialogTitle>

      {submitted ? (
        <DialogContent>
          <Alert severity="success">
            <Typography variant="body1" fontWeight={600}>
              Your request has been successfully sent. An account representative will reach out to you soon.
            </Typography>
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="contained" onClick={handleClose}>
              Close
            </Button>
          </Box>
        </DialogContent>
      ) : (
        <>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <TextField
                  label="Full Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  fullWidth
                  required
                />
                <TextField
                  label="Your Title / Position"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  fullWidth
                  required
                />
              </Box>
              <TextField
                label="Business Name"
                value={formData.businessName}
                onChange={(e) => handleInputChange('businessName', e.target.value)}
                fullWidth
                required
              />
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <TextField
                  label="Phone Number"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  fullWidth
                  required
                />
                <TextField
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  fullWidth
                  required
                />
              </Box>
              <Autocomplete
                multiple
                freeSolo
                disableCloseOnSelect
                options={KEYWORDS}
                value={formData.positions}
                onChange={(_, newValue) => handleInputChange('positions', newValue as string[])}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="What Position(s) Are You Hiring For?"
                    placeholder="Select or type a position"
                    helperText="Select from the list or type your own"
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleSubmit} variant="contained" disabled={!isFormValid || isLoading}>
              {isLoading ? 'Submitting...' : 'Submit'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default ContactRequestModal;
